'use client';

import { Suspense, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { ThemeProvider } from 'next-themes';
import { ProgressProvider } from '@bprogress/next/app';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ThemeAwareToaster } from '@/components/ThemeAwareToaster';
import { UAContentView } from '@/components/UAContentView';
import { UAPurchaseTracker } from '@/components/UAPurchaseTracker';
import { attachGlobalDoubleClickSelectionGuard } from '@/lib/dblclick-selection-guard';
import { UsageLimitProvider } from '@/hooks/use-usage-limit-handler';

/**
 * CLIENT ROOT COMPONENT
 *
 * This is the main client-side wrapper that runs on every page load.
 * It handles global concerns like auth token expiry, pageview tracking, and scroll behavior.
 */
export default function ClientRoot({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const contentView = pathname === '/usage'
    ? { contentId: 'pricing', description: 'Pricing page' }
    : pathname === '/usage/plans'
      ? { contentId: 'usage-plans', description: 'Usage plans page' }
      : null
  
  useEffect(() => {
    // Global fetch interceptor to handle expired tokens
    // When API calls return 401 (Unauthorized), it means the token is expired
    // We trigger a page refresh which will cause middleware to redirect to /
    const originalFetch = window.fetch;
    const authRoutePattern = /\/auth\/|\/signup|\/login|\/api\/auth\//;
    const RELOAD_TS_KEY = 'atrium-401-reload-ts';
    const MIN_MS_BETWEEN_401_RELOAD = 10_000;
    let last401ReloadAt = 0;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Only check on 401 - early return for all other statuses
      if (response.status === 401) {
        // Simplified URL extraction
        let url = '';
        const firstArg = args[0];
        if (firstArg instanceof Request) {
          url = firstArg.url;
        } else if (typeof firstArg === 'string') {
          url = firstArg;
        } else if (firstArg instanceof URL) {
          url = firstArg.href;
        } else if (firstArg && typeof firstArg === 'object' && 'url' in firstArg) {
          url = (firstArg as { url: string }).url;
        }
        
        // Only refresh if we're not already on an auth route to prevent loops
        if (url && !authRoutePattern.test(url)) {
          const now = Date.now();
          let last = last401ReloadAt;
          try {
            last = Math.max(last, Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0));
          } catch {
            /* private mode */
          }
          if (now - last < MIN_MS_BETWEEN_401_RELOAD) {
            return response;
          }
          last401ReloadAt = now;
          try {
            sessionStorage.setItem(RELOAD_TS_KEY, String(now));
          } catch {
            /* ignore */
          }
          console.log('Token expired detected (401), refreshing page...');
          // Refresh immediately - page reload will interrupt any further execution
          // This prevents error handling code from running with an expired token
          window.location.reload();
        }
      }
      
      return response;
    };
    
    // Cleanup: restore original fetch on unmount
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    const detach = attachGlobalDoubleClickSelectionGuard();
    return detach;
  }, []);

  // Track pageviews for analytics
  useEffect(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        page: pathname,
      });
    }
  }, [pathname]);

  // Scroll to top instantly when navigating to a new page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      enableColorScheme
      storageKey="atrium-theme-preference"
      disableTransitionOnChange
    >
      <ProgressProvider
          height="3px"
          color="var(--color-brand)"
          options={{ showSpinner: false }}
          shallowRouting
        >
          <Tooltip.Provider>
            <UsageLimitProvider>
              {children}
              <Suspense fallback={null}>
                <UAPurchaseTracker />
              </Suspense>
              {contentView && (
                <UAContentView contentId={contentView.contentId} description={contentView.description} />
              )}
              <ThemeAwareToaster />
            </UsageLimitProvider>
          </Tooltip.Provider>
      </ProgressProvider>
    </ThemeProvider>
  );
}
